import * as api from '@gitbook/api-client';
import { type SiteFeatureKey, SiteType } from '@gitbook/api-client';
import { filterOutNullable } from '@gitbook/util-ts';

import { PricingFeatureCategory } from './table_feature_categories';
import { LEGACY_SITE_PRICING_PRODUCTS, SITE_PRICING_PRODUCTS, SitePlanTarget } from './table_sites';

/**
 * Site features map.
 */
type ExtractValue<T, K extends string, S extends SiteType> = Extract<T, { key: K }> extends {
    [key in S]: infer V;
}
    ? V
    : never;
type SiteFeatureMappedValues<T extends typeof SITE_FEATURES_INPUT = typeof SITE_FEATURES_INPUT> = {
    [K in T[number]['key']]:
        | ExtractValue<T[number], K, SiteType.Basic>
        | ExtractValue<T[number], K, SiteType.Premium>
        | ExtractValue<T[number], K, SiteType.Ultimate>
        | ExtractValue<T[number], K, SiteType.LegacyBasic>
        | ExtractValue<T[number], K, SiteType.LegacyPremium>;
};

/**
 * Value associated to a site feature.
 */
type SiteFeatureValue = SiteFeatureMappedValues[SiteFeatureKey];

/**
 * Mapping of site types to their site feature.
 */
type SiteFeaturesByProduct = {
    [key in SiteType]: SiteFeatureValue;
};

/**
 * Complete definition for a site feature object.
 */
export type SiteFeatureDefinition = {
    key: SiteFeatureKey;

    // Generic name of the feature, e.g. `Unlimited traffic`
    title: string;

    // Tooltip label
    tooltipLabel: string;

    // Name of of the feature in the plans table
    plansTableTitle?: string;

    // Copy used in the upgrade dialog
    upgradeTitle?: string;
    upgradeDescription?: string;

    // Link to documentation, if available
    documentation?: string;

    // Category the feature belongs to, for grouped rendering in the plans table
    category: PricingFeatureCategory;

    // Whether this feature is visible in the org settings/plan section
    unlistedInPlansTable?: boolean;

    // Default true. Whether this feature is visible in plan cards (e.g. Change Plan dialog)
    listedInPlanCards?: boolean;
} & SiteFeaturesByProduct;

/**
 * Dynamic site feature table.
 */
const SITE_FEATURES_INPUT = [
    //
    // Usage
    //
    {
        key: api.SiteFeatureKey.SitesVisitors,
        title: 'Unlimited traffic',
        tooltipLabel: 'We never limit the amount of traffic to your published documentation.',
        plansTableTitle: 'Page views',
        upgradeTitle: 'Enjoy unlimited traffic',
        upgradeDescription: 'Remove all traffic limits and grow your audience without constraints.',
        category: PricingFeatureCategory.Usage,
        listedInPlanCards: false,

        [SiteType.Basic]: Number.POSITIVE_INFINITY,
        [SiteType.Sponsored]: Number.POSITIVE_INFINITY,
        [SiteType.Premium]: Number.POSITIVE_INFINITY,
        [SiteType.Ultimate]: Number.POSITIVE_INFINITY,
        [SiteType.LegacyBasic]: Number.POSITIVE_INFINITY,
        [SiteType.LegacyPremium]: Number.POSITIVE_INFINITY,
    },

    //
    // Features
    //
    {
        key: api.SiteFeatureKey.SitesPageTrafficInsights,
        title: 'Page view insights',
        tooltipLabel:
            'See which pages your users are viewing the most and how your site traffic compares to the previous week.',
        upgradeTitle: 'Boost your site insights',
        upgradeDescription:
            'Gain deeper insights into page performance to optimize your content strategy.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: true,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesPreviewDeployments,
        title: 'Preview deployments',
        tooltipLabel:
            'See your content edits in context with unlimited full site previews before you deploy.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: true,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesFullTextSearch,
        title: 'Full text search',
        tooltipLabel:
            'Let users search for keywords and phrases across your docs, with results that take them right to the correct page section.',
        upgradeTitle: 'Enhance search capabilities',
        upgradeDescription: 'Improve visitor experience with powerful full-text search features.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: true,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesApiPlayground,
        title: 'API playground',
        tooltipLabel: 'Let users test API endpoints directly in your docs using interactive blocks',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: true,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesUserContribution,
        title: 'User contribution',
        tooltipLabel:
            'Allow your users to propose updates and improvements to your docs — enabled via Git Sync.',
        upgradeTitle: 'Get user feedback easily',
        upgradeDescription:
            'Allow your users to propose updates and improvements to your docs — enabled via Git Sync.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: true,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesSeo,
        title: 'Built-in SEO',
        tooltipLabel:
            'GitBook handles the meta information, sitemap, responsive design and more for your site, so you don’t have to worry.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: true,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesRedirects,
        title: 'Redirect management',
        tooltipLabel:
            'Easily set up custom redirects when you migrate your content into GitBook to avoid broken links.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: false,
    },

    {
        key: api.SiteFeatureKey.SitesAdaptiveContent,
        title: 'Adaptive content',
        tooltipLabel: 'Show or hide content to specific users based on criteria you specify.',
        upgradeTitle: 'Tailor your content to meet every visitor’s needs',
        upgradeDescription:
            'Decide who sees what and when. Use your own customer data to set powerful access rules and choose which content to show or hide based on criteria.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: false,
    },

    {
        key: api.SiteFeatureKey.SitesAiSearch,
        title: 'AI search',
        plansTableTitle: 'AI-powered instant answers',
        tooltipLabel:
            'Allow site visitors to ask GitBook AI questions about the content on your site and get summarized answers in seconds.',
        upgradeTitle: 'Unlock AI-powered instant answers',
        upgradeDescription:
            'Allow all site visitors to ask GitBook AI any question and return back simple, semantic answers — with clickable references if they want to dive deeper.',
        category: PricingFeatureCategory.Features,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesAiAssistant,
        title: 'AI assistant',
        plansTableTitle: 'Interactive AI assistant',
        tooltipLabel:
            'Give your visitors a full AI assistant that can leverage third-party tools to answer questions.',
        upgradeTitle: 'Unlock the AI assistant',
        upgradeDescription:
            'Provide an interactive assistant connected to your tools for deeper answers.',
        category: PricingFeatureCategory.Features,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: false,
    },

    {
        key: api.SiteFeatureKey.SitesEmbed,
        title: 'Embedded Assistant',
        plansTableTitle: 'Embed GitBook Assistant in your product',
        tooltipLabel:
            'Make the GitBook Assistant available in your own product or site to surface knowledge exactly when your users need it.',
        upgradeTitle: 'Unlock the embedded assistant',
        upgradeDescription:
            'Make the GitBook Assistant available in your own product or site to surface knowledge exactly when your users need it.',
        category: PricingFeatureCategory.Features,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: false,
    },

    {
        key: api.SiteFeatureKey.SitesAdvancedInsights,
        title: 'Advanced insights',
        tooltipLabel:
            'See which pages your users are viewing the most and how your site traffic compares to the previous week.',
        upgradeTitle: 'Boost your site insights',
        upgradeDescription:
            'Gain deeper insights into yoru site performance to optimize your content strategy.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: false,
    },

    {
        key: api.SiteFeatureKey.SitesCustomDomain,
        title: 'Custom domain',
        tooltipLabel:
            'Publish your content with the URL of your choice (e.g. docs.yourcompany.com).',
        upgradeTitle: 'Use your own domain',
        upgradeDescription:
            'Make your docs site more reachable and recognizable with any custom domain you own.',
        category: PricingFeatureCategory.Features,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesNoCustomDomain,
        title: 'gitbook.io domain only',
        tooltipLabel: 'Publish your site under the gitbook.io domain.',
        category: PricingFeatureCategory.Features,
        unlistedInPlansTable: true,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: false,
        [SiteType.LegacyBasic]: true,
        [SiteType.LegacyPremium]: false,
    },

    {
        key: api.SiteFeatureKey.SitesCustomSubdirectory,
        title: 'Custom subdirectory',
        tooltipLabel: 'Publish your content on a subdirectory (e.g. yourcompany.com/docs).',
        upgradeTitle: 'Host your docs on a subdirectory',
        upgradeDescription:
            'Publish your content on a subdirectory of your domain to keep your brand consistent.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: false,
    },

    {
        key: api.SiteFeatureKey.SitesSearchInsights,
        title: 'Search insights',
        tooltipLabel:
            'See what your users are searching for on your site to help identify content gaps.',
        upgradeTitle: 'Detailed search analytics',
        upgradeDescription:
            'Understand what users are searching for to better cater to their needs.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesPageFeedback,
        title: 'User feedback',
        tooltipLabel:
            'Understand how useful your content is by allowing your users to rate each page.',
        upgradeTitle: 'Get user feedback easily',
        upgradeDescription:
            'Collect valuable insights from users to improve your content and services.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesPdfExport,
        title: 'PDF export',
        tooltipLabel:
            'Allow your site visitors to easily export pages of your content in PDF format.',
        upgradeTitle: 'Allow easy PDF site exports',
        upgradeDescription:
            'Let users export pages as PDFs with a click for offline access and distribution.',
        category: PricingFeatureCategory.Features,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: true,
    },

    //
    // Sharing & visibility
    //
    {
        key: api.SiteFeatureKey.SitesUnlistedVisibility,
        title: 'Hide from search engines',
        tooltipLabel:
            'Create a docs site that is not indexed by search engines. Ideal for publishing docs for things like beta programs or user testing.',
        upgradeTitle: 'Opt for unlisted visibility',
        upgradeDescription:
            'Choose to publish your site publicly, but not appear in search engine results to control access and privacy.',
        category: PricingFeatureCategory.Sharing,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesShareLinks,
        title: 'Private share links',
        tooltipLabel:
            'Publish a docs site that is only accessible to people with a unique link, which can be revoked at any time.',
        upgradeTitle: 'Share content more privately',
        upgradeDescription:
            'Create unique links to your content to share with specific people, and revoke them at any time.',
        category: PricingFeatureCategory.Sharing,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesVisitorAuthentication,
        title: 'Authenticated access',
        tooltipLabel:
            'Use an authentication integration or server-side code to control which specific users have access your documentation.',
        upgradeTitle: 'Secure your content',
        upgradeDescription: 'Control access to your content with robust authentication options.',
        category: PricingFeatureCategory.Sharing,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: true,
    },

    //
    // Configuration
    //
    {
        key: api.SiteFeatureKey.SitesMultivariantSite,
        title: 'Site variants',
        tooltipLabel:
            'Publish multiple spaces on the same site to support localization and version releases for the same product.',
        category: PricingFeatureCategory.Configuration,
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: true,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesBasicCustomization,
        title: 'Basic customization',
        tooltipLabel:
            'Customize your site’s primary color and corner style, and enable a light/dark mode toggle.',
        category: PricingFeatureCategory.Configuration,
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: true,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesAdvancedCustomization,
        title: 'Advanced customization',
        tooltipLabel:
            'Change your site’s logo, font, footer, and primary/header/header link colors to create highly branded docs.',
        upgradeTitle: 'Add your brand’s flair',
        upgradeDescription:
            'Access more design and functionality options to tailor your site perfectly.',
        category: PricingFeatureCategory.Configuration,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesCustomFonts,
        title: 'Custom fonts',
        tooltipLabel: 'Upload your own custom fonts to use in your docs sites.',
        upgradeTitle: 'Add custom fonts',
        upgradeDescription: 'Upload your own custom fonts to use in your docs sites.',
        category: PricingFeatureCategory.Configuration,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: false,
    },

    {
        key: api.SiteFeatureKey.SitesSections,
        title: 'Site sections',
        tooltipLabel:
            'Split your documentation into distinct sections or group the documentation of multiple products into a single site.',
        upgradeTitle: 'Get your docs in order',
        upgradeDescription:
            'Divide your site into sections to showcase every part of your product on a single docs site.',
        category: PricingFeatureCategory.Configuration,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: false,
    },

    //
    // Unlisted
    //
    {
        key: api.SiteFeatureKey.SitesPublicVisibility,
        title: 'Public visibility',
        tooltipLabel: 'Publish your docs site to everyone, and index it in search engines.',
        upgradeTitle: 'Maximize site visibility',
        upgradeDescription:
            'Ensure your site is visible to everyone, increasing your reach and impact.',
        category: PricingFeatureCategory.Unlisted,
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
        [SiteType.LegacyBasic]: true,
        [SiteType.LegacyPremium]: true,
    },

    {
        key: api.SiteFeatureKey.SitesAds,
        title: 'Site ads',
        tooltipLabel: 'Generate revenue with ads',
        upgradeTitle: 'Monetize your site',
        upgradeDescription:
            'Monetize your site by displaying ads to your visitors and earn revenue.',
        category: PricingFeatureCategory.Unlisted,
        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: false,
        [SiteType.LegacyBasic]: false,
        [SiteType.LegacyPremium]: false,
    },
] as const;

// SITE_FEATURES_INPUT are untyped arrays so that SiteFeatureKey can be extracted as an union.
// We want to validate that SITE_FEATURES is a valid array of SiteFeatureDefinition.
export const SITE_FEATURES: readonly SiteFeatureDefinition[] = SITE_FEATURES_INPUT;

export const SITE_FEATURES_MAP = SITE_FEATURES.reduce(
    (acc, feature) => {
        acc[feature.key] = feature;
        return acc;
    },
    {} as {
        [key in SiteFeatureKey]: SiteFeatureDefinition;
    }
);

/**
 * Maps a list of api.SiteCustomizationFeature keys to a readable list of
 * advanced customization settings
 */
export function mapAdvancedCustomizationsToReadableLabels(
    advancedCustomizations: api.SiteCustomizationFeature[]
): string[] {
    const AdvancedCustomizationsLabels: Record<api.SiteCustomizationFeature, string> = {
        [api.SiteCustomizationFeature.HeaderLogo]: 'Header logo',
        [api.SiteCustomizationFeature.HeaderPrimaryLink]: 'Header primary link',
        [api.SiteCustomizationFeature.ThemePreset]: 'Theme preset',
        [api.SiteCustomizationFeature.PremiumFonts]: 'Premium fonts',
        [api.SiteCustomizationFeature.CustomIcons]: 'Custom icons',
        [api.SiteCustomizationFeature.FooterLogo]: 'Footer logo',
        [api.SiteCustomizationFeature.FooterLinks]: 'Footer links',
        [api.SiteCustomizationFeature.FooterCopyright]: 'Footer copyright',
        [api.SiteCustomizationFeature.SemanticColors]: 'Semantic colors',
    };

    return advancedCustomizations
        .map((customizationId) => AdvancedCustomizationsLabels[customizationId])
        .filter(filterOutNullable);
}

/**
 * Get the minimum site subscription required for a given feature.
 */
function getMinimumSiteSubscriptionForFeature(
    isNewPricing: boolean,
    featureKey: SiteFeatureKey,
    defaultSiteType: SiteType
): SiteType {
    const feature = SITE_FEATURES_MAP[featureKey];
    const allSiteSubscriptions = (
        isNewPricing
            ? [
                  feature[SiteType.Basic] ? SiteType.Basic : null,
                  // TODO: For now we are only promoting upsell when upgrading from Sponsored.
                  // But we should change this and may have to revisit the data structure of
                  // site/features tables.
                  feature[SiteType.Sponsored] && defaultSiteType === api.SiteType.Sponsored
                      ? SiteType.Sponsored
                      : null,
                  feature[SiteType.Premium] ? SiteType.Premium : null,
                  feature[SiteType.Ultimate] ? SiteType.Ultimate : null,
              ]
            : [
                  feature[SiteType.LegacyBasic] ? SiteType.LegacyBasic : null,
                  feature[SiteType.LegacyPremium] ? SiteType.LegacyPremium : null,
              ]
    ).filter(filterOutNullable);
    return allSiteSubscriptions[0];
}

/**
 * Get the minimum site subscription required for a list of features.
 * Returns null if the feature is not available on any plan.
 */
export function getMinimumSiteSubscriptionForFeatures(input: {
    isNewPricing: boolean;
    featureKeys: SiteFeatureKey[];
    defaultSiteType: SiteType;
}): SiteType | null {
    const { isNewPricing, featureKeys, defaultSiteType } = input;
    const siteProducts = isNewPricing
        ? // TODO: For now we are only promoting upsell when upgrading from Sponsored.
          // But we should change this and may have to revisit the data structure of
          // site/features tables.
          defaultSiteType === SiteType.Sponsored
            ? SITE_PRICING_PRODUCTS
            : SITE_PRICING_PRODUCTS.filter((product) =>
                  product.targets.includes(SitePlanTarget.CUSTOMERS)
              )
        : LEGACY_SITE_PRICING_PRODUCTS;
    const siteTypes = siteProducts.map(({ key }) => key);
    return featureKeys.reduce((acc, key) => {
        const minimumSiteSubscription = getMinimumSiteSubscriptionForFeature(
            isNewPricing,
            key,
            defaultSiteType
        );
        if (siteTypes.indexOf(minimumSiteSubscription) > siteTypes.indexOf(acc)) {
            return minimumSiteSubscription;
        }
        return acc;
    }, null);
}

// Returns unique features for any site type for either legacy or new pricing. Each array only includes features that the given site type unlocks, so any features from a lower type are *not* included.
export function getFeaturesPerSiteType() {
    return {
        [SiteType.LegacyBasic]: SITE_FEATURES_INPUT.filter(
            (feature) => feature[SiteType.LegacyBasic]
        ),
        [SiteType.LegacyPremium]: SITE_FEATURES_INPUT.filter(
            (feature) => feature[SiteType.LegacyPremium] && !feature[SiteType.LegacyBasic]
        ),
        [SiteType.Basic]: SITE_FEATURES_INPUT.filter((feature) => feature[SiteType.Basic]),
        [SiteType.Sponsored]: SITE_FEATURES_INPUT.filter(
            (feature) => feature[SiteType.Sponsored] && !feature[SiteType.Basic]
        ),
        [SiteType.Premium]: SITE_FEATURES_INPUT.filter(
            (feature) => feature[SiteType.Premium] && !feature[SiteType.Basic]
        ),
        [SiteType.Ultimate]: SITE_FEATURES_INPUT.filter(
            (feature) =>
                feature[SiteType.Ultimate] && !feature[SiteType.Premium] && !feature[SiteType.Basic]
        ),
    };
}
